import { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL2070_R01Store } from "./Store_SAL2070_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultSAL2070_R01Dto, SAL2070_R01Dto } from "./Dto_SAL2070_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2070_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2070_R01Dto> | null>;
	defaultSAL2070_R01HeaderRow: SAL2070_R01Dto;
}

export const Table_SAL2070_R01 = (props: Props) => {

	const { gridRef, defaultSAL2070_R01HeaderRow } = props;

	const { SAL2070_R01List, 
		selectedSAL2070_R01, setSelectedSAL2070_R01, initSelectedSAL2070_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSAL2070_R01Store();

	const { vatTypeList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL2070_R01List,
		defaultGridData: defaultSAL2070_R01HeaderRow,
		selectedRow: selectedSAL2070_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		tradeDt: '거래일자',
		customNm: '거래처',
		vatType: '세율구분',
		billAmt: '거래금액',
		tradeType: '거래구분',
		taxBilling: '발행여부',	
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'valueIndex')(params), []);

	const baseColumnDefs: ColDef<SAL2070_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '거래일자', colId: 'tradeDt', field: 'tradeDt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'tradeDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: ValueFormatterParams<SAL2070_R01Dto>) =>
				p.data?.tradeDt ? formatDate(new Date(p.data.tradeDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처', colId: 'customNm', field: 'customNm', width: 130,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '세율구분', colId: 'vatType', field: 'vatType', width: 90,
			cellStyle: (p) => getCellStyle(p, 'vatType'),
			...CommonCodeColumn.options(vatTypeList, true, undefined, undefined),
		},
		{
			headerName: '거래금액', colId: 'totalAmt', field: 'totalAmt', width: 130,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			editable: false,
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '거래구분', colId: 'tradeType', field: 'tradeType', width: 75,
			cellStyle: (p) => getCellStyle(p, 'tradeType'),
			editable: false,
			headerClass: aggridHeaderClass,
			valueFormatter: (params: ValueFormatterParams<SAL2070_R01Dto>) => {
				if (params.node?.rowPinned) {
					return '';
				}
				return params.value === 'SAL' ? '매출' : '매입';
			},
		},
		{
			headerName: '발행여부', colId: 'taxBilling', field: 'taxBilling', width: 75,
			cellStyle: (p) => getCellStyle(p, 'taxBilling'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params: ValueFormatterParams<SAL2070_R01Dto>) => {
				if (params.node?.rowPinned) {
					return '';
				}
				return params.value === 'Y' ? '발행' : '미발행';
			},
		},
		{
			headerName: '담당자', colId: 'taxEmpNm', field: 'taxEmpNm', width: 90,
			cellStyle: (p) => getCellStyle(p, 'taxEmpNm'),
			headerClass: aggridHeaderClass,
		},
	], [getCellStyle, cellDateComparator, vatTypeList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2070_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2070_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	const handleRowClick = useCallback((e: RowClickedEvent<SAL2070_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedSAL2070_R01?.valueIndex === rowData.valueIndex;
		
		if (isSelected) {
			setDetailScrollTop(null);
			setSelectedSAL2070_R01(undefined);
		} else {
			setDetailScrollTop(null);
			setSelectedSAL2070_R01(rowData);
		}
	}, [selectedSAL2070_R01, setSelectedSAL2070_R01, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => {

		const totalAmt : number = SAL2070_R01List
			.reduce((sum, item) => sum + Number(item.totalAmt) || 0, 0);

		return [{
			rowIndex: '',
			totalAmt: totalAmt.toLocaleString(),
			editable: false,
		}]
	}, [SAL2070_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL2070_R01List, selectedSAL2070_R01]);

	return (
		
		<GridContextWrapper<SAL2070_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL2070_R01}
			defaultRowData={defaultSAL2070_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL2070_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<SAL2070_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSAL2070_R01 ? [selectedSAL2070_R01.valueIndex] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
